import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, ArrowRight, Plus, Trash2, CheckCircle, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { canWrite, canDelete } from '../utils/permissions';
import { formatDate } from '../utils/dateFormatter';
import { jobEarned, workerEarned, jobRemaining } from '../utils/outsourcingEarnings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status, t }) => {
  const styles = {
    assigned: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    in_progress: 'bg-orange-100 text-orange-800 border border-orange-300',
    completed: 'bg-blue-100 text-blue-800 border border-blue-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
      {t(status)}
    </span>
  );
};

const StatCard = ({ label, value, color, language }) => (
  <Card className="p-4">
    <p className="text-xs text-muted-foreground mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{label}</p>
    <p className={`text-2xl font-bold ${color || ''}`}>{value}</p>
  </Card>
);

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between gap-2 py-1 border-b border-border/40 last:border-0">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="font-medium text-sm text-end">{value}</span>
  </div>
);

export const OutsourcingWorker = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();

  const [worker, setWorker] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit worker dialog
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', notes: '' });

  // Inline accordion for job details
  const [expandedJobId, setExpandedJobId] = useState(null);
  const toggleJob = (jobId) => setExpandedJobId(prev => prev === jobId ? null : jobId);

  // New job dialog
  const [isJobOpen, setIsJobOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeQty, setCompleteQty] = useState('');
  const [completeDate, setCompleteDate] = useState(new Date().toISOString().split('T')[0]);
  const [jobForm, setJobForm] = useState({ order_id: '', order_number: '', item_code: '', description: '', work_type: 'full', quantity: '', cost_per_piece: '', assigned_date: new Date().toISOString().split('T')[0], card_number: '' });
  const [selectedOrderTotal, setSelectedOrderTotal] = useState(null);
  const [selectedOrderAssigned, setSelectedOrderAssigned] = useState(0);
  const [overrideCap, setOverrideCap] = useState(false);

  // Payment dialog
  const [isPayOpen, setIsPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });

  const loadData = async () => {
    try {
      const [wRes, jRes, pRes, oRes, rRes, allJobsRes] = await Promise.all([
        axios.get(`${API}/outsourcing/workers/${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/jobs?worker_id=${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/payments?worker_id=${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/orders`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/rates`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/jobs`, { headers: getAuthHeader() }),
      ]);
      setWorker(wRes.data);
      setJobs(jRes.data);
      setPayments(pRes.data);
      // Keep every order: the double-operation badge below looks up piece
      // counts here and must find fulfilled orders too. The new-job dropdown
      // narrows to open orders via `openOrders`.
      setOrders(oRes.data);
      setRates(rRes.data);
      setAllJobs(allJobsRes.data);
    } catch {
      toast.error('Failed to load worker data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [id]);

  // Earned, not "completed": a job finished 27 of 105 owes for those 27.
  const totalWork = workerEarned(jobs, id);
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = totalWork - totalPaid;

  // Order IDs where total assigned quantity exceeds the order total (double-operation override)
  const sharedOrderIds = useMemo(() => {
    const orderTotals = {};
    orders.forEach(o => {
      orderTotals[o.id] = (o.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    });
    const orderAssigned = {};
    allJobs.forEach(j => {
      if (!j.order_id) return;
      orderAssigned[j.order_id] = (orderAssigned[j.order_id] || 0) + j.quantity;
    });
    return new Set(
      Object.entries(orderAssigned)
        .filter(([id, assigned]) => orderTotals[id] !== undefined && assigned > orderTotals[id])
        .map(([k]) => k)
    );
  }, [allJobs, orders]);

  // Only open orders can take new work. Kept separate from `orders` because the
  // badge above needs the full list — filtering at the source silently killed it
  // for every fulfilled order, which is every double-operation order eventually.
  const openOrders = useMemo(
    () => orders.filter(o => ['pending', 'in_production'].includes(o.status)),
    [orders]
  );

  // Sort jobs by assigned_date descending (newest first)
  const sortedJobs = [...jobs].sort((a, b) =>
    (b.assigned_date || '').localeCompare(a.assigned_date || '')
  );

  // --- Edit worker ---
  const openEdit = () => {
    setEditForm({ name: worker.name, phone: worker.phone || '', notes: worker.notes || '' });
    setIsEditOpen(true);
  };

  const handleSaveWorker = async () => {
    if (!editForm.name.trim()) { toast.error(t('workerNameRequired')); return; }
    try {
      await axios.put(`${API}/outsourcing/workers/${id}`, editForm, { headers: getAuthHeader() });
      toast.success(t('workerUpdated'));
      setIsEditOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update worker');
    }
  };

  // --- New job ---
  const handleOrderChange = async (orderId) => {
    setOverrideCap(false);
    const order = orders.find(o => o.id === orderId);
    if (!order) {
      setJobForm(f => ({ ...f, order_id: '', order_number: '', item_code: '', description: '', cost_per_piece: '', card_number: '' }));
      setSelectedOrderTotal(null);
      setSelectedOrderAssigned(0);
      return;
    }
    const orderTotal = (order.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    try {
      const allJobsRes = await axios.get(`${API}/outsourcing/jobs?order_id=${order.id}`, { headers: getAuthHeader() });
      const alreadyAssigned = (allJobsRes.data || []).reduce((s, j) => s + (j.quantity || 0), 0);
      setSelectedOrderAssigned(alreadyAssigned);
    } catch {
      setSelectedOrderAssigned(0);
    }
    setSelectedOrderTotal(orderTotal);
    const rate = rates.find(r => r.item_code === order.item_code);
    setJobForm(f => ({
      ...f,
      order_id: order.id,
      order_number: order.order_number,
      item_code: order.item_code,
      description: order.items || '',
      cost_per_piece: rate ? rate.cost_per_piece : f.cost_per_piece,
      card_number: order.card_number || '',
    }));
  };

  const openJob = () => {
    setJobForm({ order_id: '', order_number: '', item_code: '', description: '', work_type: 'full', quantity: '', cost_per_piece: '', assigned_date: new Date().toISOString().split('T')[0], card_number: '' });
    setSelectedOrderTotal(null);
    setSelectedOrderAssigned(0);
    setOverrideCap(false);
    setIsJobOpen(true);
  };

  const handleCreateJob = async () => {
    const hasOrder = !!jobForm.order_id;
    if (hasOrder && (!jobForm.quantity || isNaN(jobForm.quantity) || parseInt(jobForm.quantity) <= 0)) { toast.error(t('enterValidQuantity')); return; }
    if (hasOrder && selectedOrderTotal !== null && !overrideCap) {
      const remaining = selectedOrderTotal - selectedOrderAssigned;
      if (parseInt(jobForm.quantity) > remaining) {
        toast.error(`Only ${remaining} pieces remaining for this order (total: ${selectedOrderTotal})`);
        return;
      }
    }
    if (!jobForm.cost_per_piece || isNaN(jobForm.cost_per_piece)) { toast.error(t('enterValidCost')); return; }
    const qty = jobForm.quantity && !isNaN(jobForm.quantity) && parseInt(jobForm.quantity) > 0 ? parseInt(jobForm.quantity) : 1;
    const payload = {
      worker_id: id,
      worker_name: worker.name,
      order_id: jobForm.order_id || null,
      order_number: jobForm.order_number,
      item_code: jobForm.item_code,
      description: jobForm.description,
      work_type: jobForm.work_type,
      quantity: qty,
      cost_per_piece: parseFloat(jobForm.cost_per_piece),
      assigned_date: jobForm.assigned_date,
      skip_quantity_check: overrideCap,
    };
    try {
      await axios.post(`${API}/outsourcing/jobs`, payload, { headers: getAuthHeader() });
      toast.success(t('jobCreated'));
      setIsJobOpen(false);
      setOverrideCap(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create job');
    }
  };

  // Pieces are only ever added, so the box starts empty and means "how many
  // more are finished" — never a new total.
  const openComplete = (job) => {
    setCompleteTarget(job);
    setCompleteQty('');
    setCompleteDate(new Date().toISOString().split('T')[0]);
  };

  const handleComplete = async () => {
    const qty = parseInt(completeQty, 10);
    if (isNaN(qty) || qty <= 0) { toast.error(t('enterValidQuantity')); return; }
    const remaining = jobRemaining(completeTarget);
    if (qty > remaining) {
      toast.error(`${t('remainingPieces')}: ${remaining}`);
      return;
    }
    if (!completeDate) { toast.error(t('dateFinished')); return; }
    try {
      await axios.post(`${API}/outsourcing/jobs/${completeTarget.id}/progress`,
        { quantity: qty, date: completeDate }, { headers: getAuthHeader() });
      toast.success(qty >= remaining ? t('jobCompleted') : t('progressSaved'));
      setCompleteTarget(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record progress');
    }
  };

  const handleDeleteEntry = async (job, entry) => {
    if (!window.confirm(`${t('deleteEntryConfirm')} ${entry.quantity} (${entry.date || '—'})?`)) return;
    try {
      await axios.delete(`${API}/outsourcing/jobs/${job.id}/progress/${entry.id}`, { headers: getAuthHeader() });
      toast.success(t('entryDeleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove entry');
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Delete job ${job.job_number}?`)) return;
    try {
      await axios.delete(`${API}/outsourcing/jobs/${job.id}`, { headers: getAuthHeader() });
      toast.success(t('jobDeleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete job');
    }
  };

  // --- Payments ---
  const openPay = () => {
    setPayForm({ amount: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });
    setIsPayOpen(true);
  };

  const handleAddPayment = async () => {
    if (!payForm.amount || isNaN(payForm.amount) || parseFloat(payForm.amount) <= 0) { toast.error(t('enterValidAmount')); return; }
    if (!payForm.payment_date) { toast.error(t('paymentDateRequired')); return; }
    try {
      await axios.post(`${API}/outsourcing/payments`, {
        worker_id: id,
        worker_name: worker.name,
        amount: parseFloat(payForm.amount),
        payment_date: payForm.payment_date,
        notes: payForm.notes || null,
      }, { headers: getAuthHeader() });
      toast.success(t('paymentRecorded'));
      setIsPayOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleDeletePayment = async (p) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/outsourcing/payments/${p.id}`, { headers: getAuthHeader() });
      toast.success(t('paymentDeleted'));
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payment');
    }
  };

  if (loading) return (
    <div className="p-8 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
      {t('loading')}
    </div>
  );
  if (!worker) return (
    <div className="p-8 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
      {t('workerNotFound')}
    </div>
  );

  const liveCost = (parseFloat(jobForm.quantity) || 0) * (parseFloat(jobForm.cost_per_piece) || 0);
  const remaining = selectedOrderTotal !== null ? selectedOrderTotal - selectedOrderAssigned : null;
  const fullyAssigned = remaining !== null && remaining <= 0;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/outsourcing')}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            {language === 'ar' ? <ArrowRight className="w-3.5 h-3.5" /> : <ArrowLeft className="w-3.5 h-3.5" />}
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('backToOutsourcing')}</span>
          </button>
          <h1 className="text-xl md:text-3xl font-bold" lang={language === 'ar' ? 'ar' : 'en'}>{worker.name}</h1>
          {worker.phone && <p className="text-muted-foreground mt-0.5">{worker.phone}</p>}
          {worker.notes && (
            <p className="text-sm text-muted-foreground mt-1 italic" lang={language === 'ar' ? 'ar' : 'en'}>
              {worker.notes}
            </p>
          )}
        </div>
        {canWrite(user?.role) && <Button variant="outline" size="sm" onClick={openEdit}>
          <Pencil className="w-3.5 h-3.5 me-1" />
          <span lang={language === 'ar' ? 'ar' : 'en'}>{t('edit')}</span>
        </Button>}
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6 md:mb-8">
        <StatCard label={t('totalWorkValue')} value={`${totalWork.toFixed(2)} ${t('currency')}`} language={language} />
        <StatCard label={t('totalPaid')} value={`${totalPaid.toFixed(2)} ${t('currency')}`} color="text-green-700" language={language} />
        <StatCard label={t('balanceRemaining')} value={`${balance.toFixed(2)} ${t('currency')}`} color={balance > 0 ? 'text-orange-600' : 'text-muted-foreground'} language={language} />
      </div>

      {/* Side-by-side: Jobs | Payments */}
      <div className="flex flex-col md:flex-row gap-6 items-start">

        {/* Jobs section */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('jobs')}</h2>
            {canWrite(user?.role) && <Button size="sm" onClick={openJob}>
              <Plus className="w-4 h-4 me-1" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('newJob')}</span>
            </Button>}
          </div>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')}</th>
                  <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('quantity')}</th>
                  <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPc')}</th>
                  <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')}</th>
                  <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('status')}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                      {t('noJobsYet')}
                    </td>
                  </tr>
                )}
                {sortedJobs.map(j => (
                  <React.Fragment key={j.id}>
                  <tr
                    className="border-b hover:bg-muted/20 cursor-pointer"
                    onClick={() => toggleJob(j.id)}
                  >
                    <td className="p-3 font-mono text-xs">
                      <div className="flex items-center gap-1">
                        {expandedJobId === j.id
                          ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                        {j.item_code || '—'}
                      </div>
                    </td>
                    <td className="p-3 text-end" dir="ltr">
                      {(j.completed_quantity || 0) > 0 && (j.completed_quantity || 0) < j.quantity
                        ? <span><span className="font-medium">{j.completed_quantity}</span> / {j.quantity}</span>
                        : j.quantity}
                    </td>
                    <td className="p-3 text-end" dir="ltr">{parseFloat(j.cost_per_piece || 0).toFixed(2)}</td>
                    {/* Earned, not the assignment value: this page is about what is owed. */}
                    <td className="p-3 text-end font-medium" dir="ltr">{jobEarned(j).toFixed(2)}</td>
                    <td className="p-3"><StatusBadge status={j.status} t={t} /></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        {/* Only while pieces are outstanding — a finished job
                            has nothing left to add. */}
                        {canWrite(user?.role) && jobRemaining(j) > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-blue-600 hover:text-blue-700"
                            title={t('addFinishedPieces')}
                            onClick={e => { e.stopPropagation(); openComplete(j); }}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {canDelete(user?.role) && (j.completed_quantity || 0) === 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={e => { e.stopPropagation(); handleDeleteJob(j); }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedJobId === j.id && (
                    <tr className="bg-muted/10 border-b">
                      <td colSpan={6} className="px-6 py-3">
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0.5 text-sm">
                          <DetailRow label={t('jobNumber')} value={j.job_number} />
                          <DetailRow label={t('orderNumber')} value={j.order_number || '—'} />
                          <DetailRow label={t('description')} value={j.description || '—'} />
                          <DetailRow label={t('workType')} value={t(j.work_type)} />
                          <DetailRow label={t('assignedQuantity')} value={<span dir="ltr">{j.quantity}</span>} />
                          <DetailRow label={t('finishedPieces')} value={<span dir="ltr">{j.completed_quantity || 0}</span>} />
                          <DetailRow label={t('remainingPieces')} value={<span dir="ltr">{jobRemaining(j)}</span>} />
                          <DetailRow label={t('assignmentValue')} value={<span dir="ltr">{parseFloat(j.total_cost || 0).toFixed(2)}</span>} />
                          <DetailRow label={t('earned')} value={<span dir="ltr">{jobEarned(j).toFixed(2)}</span>} />
                          <DetailRow label={t('assignedDate')} value={j.assigned_date ? formatDate(j.assigned_date) : '—'} />
                          <DetailRow label={t('lastProgressDate')} value={j.last_progress_date ? formatDate(j.last_progress_date) : '—'} />
                          <DetailRow label={t('completedDate')} value={j.completed_date ? formatDate(j.completed_date) : '—'} />
                          {j.card_number && <DetailRow label="Card" value={j.card_number} />}
                          {j.notes && <DetailRow label={t('notes')} value={j.notes} />}
                          {/* Each batch of finished pieces with its own date,
                              newest first. */}
                          <div className="col-span-2 mt-3">
                            <p className="text-xs font-medium text-muted-foreground mb-1" lang={language === 'ar' ? 'ar' : 'en'}>
                              {t('progressEntries')}
                            </p>
                            {(j.progress_entries || []).length === 0 ? (
                              <p className="text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                                {t('noProgressYet')}
                              </p>
                            ) : (
                              <div className="space-y-0.5">
                                {[...(j.progress_entries || [])]
                                  .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                                  .map(e => (
                                    <div key={e.id} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-border/40 last:border-0">
                                      <span className="text-muted-foreground">
                                        {e.date ? formatDate(e.date) : '—'}
                                        {e.migrated && <span className="ms-1 opacity-60">({t('migratedEntry')})</span>}
                                      </span>
                                      <span className="flex items-center gap-2">
                                        <span className="font-medium" dir="ltr">
                                          {e.quantity} {t('pieces')}
                                        </span>
                                        {/* Carried-over entries hold their own
                                            value, because four legacy rows have
                                            hand-entered totals that disagree
                                            with rate x quantity. */}
                                        <span className="text-muted-foreground" dir="ltr">
                                          {(e.value != null
                                            ? Number(e.value)
                                            : e.quantity * (Number(j.cost_per_piece) || 0)).toFixed(2)}
                                        </span>
                                        {user?.role === 'admin' && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 text-destructive hover:text-destructive"
                                            title={t('deleteEntry')}
                                            onClick={ev => { ev.stopPropagation(); handleDeleteEntry(j, e); }}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                          {j.order_id && sharedOrderIds.has(j.order_id) && (
                            <div className="col-span-2 mt-2 flex items-center gap-2 text-xs text-orange-700">
                              <span className="font-medium">×2</span>
                              <span>Multiple workers on this order</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

        {/* Payments section */}
        <div className="w-full md:w-80 md:shrink-0">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('payments')}</h2>
            {canWrite(user?.role) && <Button size="sm" onClick={openPay}>
              <Plus className="w-4 h-4 me-1" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('addPayment')}</span>
            </Button>}
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
                  <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')})</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                      {t('noPaymentsYet')}
                    </td>
                  </tr>
                )}
                {payments.map(p => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="p-3 text-xs">{formatDate(p.payment_date)}</td>
                    <td className="p-3 text-end font-medium text-green-700">{parseFloat(p.amount).toFixed(2)}</td>
                    <td className="p-3">
                      {canDelete(user?.role) && <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDeletePayment(p)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {payments.length > 0 && (
                <tfoot className="border-t bg-muted/20">
                  <tr>
                    <td className="p-3 text-xs font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalPaidOf')}</td>
                    <td className="p-3 text-end font-bold text-green-700">{totalPaid.toFixed(2)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </Card>
        </div>
      </div>

      {/* Edit worker dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('editWorker')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')} *</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('phone')}</Label>
              <Input value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSaveWorker} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('saveChanges')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New job dialog */}
      <Dialog open={isJobOpen} onOpenChange={open => { setIsJobOpen(open); if (!open) setOverrideCap(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('newJobFor')} {worker.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={jobForm.order_id}
                onChange={e => handleOrderChange(e.target.value)}
              >
                <option value="">— {t('selectOrder')} —</option>
                {openOrders.map(o => (
                  <option key={o.id} value={o.id}>{o.order_number} / {o.item_code}{o.card_number ? ` / ${o.card_number}` : ''}</option>
                ))}
              </select>
              {jobForm.card_number && (
                <p className="text-xs text-muted-foreground mt-1">Card: <span className="font-medium">{jobForm.card_number}</span></p>
              )}
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')}</Label>
              <Input
                value={jobForm.item_code}
                onChange={e => setJobForm(f => ({ ...f, item_code: e.target.value }))}
                placeholder={t('autoFilledFromOrder')}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('workType')}</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={jobForm.work_type}
                onChange={e => setJobForm(f => ({ ...f, work_type: e.target.value }))}
              >
                <option value="full">{t('full')}</option>
                <option value="partial">{t('partial')}</option>
              </select>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('quantity')}{jobForm.order_id ? ' *' : ''}</Label>
              <Input
                type="number"
                min="1"
                max={selectedOrderTotal !== null ? (overrideCap ? selectedOrderTotal : selectedOrderTotal - selectedOrderAssigned) : undefined}
                value={jobForm.quantity}
                onChange={e => setJobForm(f => ({ ...f, quantity: e.target.value }))}
                placeholder="0"
              />
              {selectedOrderTotal !== null && (
                <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>
                  {selectedOrderAssigned} / {selectedOrderTotal} {t('piecesAlreadyAssigned')} —{' '}
                  <span className="font-medium">{selectedOrderTotal - selectedOrderAssigned} {t('remaining')}</span>
                </p>
              )}
              {fullyAssigned && (
                <div className="flex items-start gap-2 p-2 mt-1 rounded-md" style={{background:'#fefce8',border:'1px solid #fde68a'}}>
                  <input
                    type="checkbox"
                    id="overrideCap"
                    checked={overrideCap}
                    onChange={e => setOverrideCap(e.target.checked)}
                    className="mt-0.5 cursor-pointer"
                  />
                  <label htmlFor="overrideCap" className="text-xs cursor-pointer" style={{color:'#92400e'}} lang={language === 'ar' ? 'ar' : 'en'}>
                    All pieces are already assigned. Check to confirm this is a different operation on the same pieces.
                  </label>
                </div>
              )}
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</Label>
              <Input
                value={jobForm.description}
                onChange={e => setJobForm(f => ({ ...f, description: e.target.value }))}
                placeholder={t('autoFilledFromOrder')}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPiece')} ({t('currency')}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={jobForm.cost_per_piece}
                onChange={e => setJobForm(f => ({ ...f, cost_per_piece: e.target.value }))}
                placeholder={t('autoFilledFromRates')}
              />
            </div>
            {liveCost > 0 && (
              <p className="text-sm font-medium text-end" lang={language === 'ar' ? 'ar' : 'en'}>
                {t('totalCostLabel')} <span className="text-primary">{liveCost.toFixed(2)} {t('currency')}</span>
              </p>
            )}
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</Label>
              <Input type="date" value={jobForm.assigned_date} onChange={e => setJobForm(f => ({ ...f, assigned_date: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => { setIsJobOpen(false); setOverrideCap(false); }} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button
                onClick={handleCreateJob}
                disabled={fullyAssigned && !overrideCap}
                lang={language === 'ar' ? 'ar' : 'en'}
              >
                {t('createJob')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add payment dialog */}
      <Dialog open={isPayOpen} onOpenChange={setIsPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addPayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')} *</Label>
              <Input
                type="date"
                value={payForm.payment_date}
                onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
              />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={payForm.amount}
                onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
              />
              <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>
                {t('balanceRemainingColon')} <span className="font-medium">{balance.toFixed(2)} {t('currency')}</span>
              </p>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsPayOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleAddPayment} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('recordPayment')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record progress dialog — an absolute tally of finished pieces, so the
          same dialog records new work and corrects a figure entered wrongly. */}
      <Dialog open={!!completeTarget} onOpenChange={open => { if (!open) setCompleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addFinishedPieces')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {completeTarget && (
              <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                {completeTarget.job_number} · {completeTarget.order_number || '—'}
              </p>
            )}
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('piecesToAdd')} *</Label>
              <Input
                type="number"
                min="1"
                max={jobRemaining(completeTarget)}
                value={completeQty}
                onChange={e => setCompleteQty(e.target.value)}
                dir="ltr"
                autoFocus
              />
              {completeTarget && (
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5" lang={language === 'ar' ? 'ar' : 'en'}>
                  <p>{t('assignedQuantity')}: <span dir="ltr">{completeTarget.quantity}</span> · {t('finishedPieces')}: <span dir="ltr">{completeTarget.completed_quantity || 0}</span></p>
                  <p className="font-medium">{t('remainingPieces')}: <span dir="ltr">{jobRemaining(completeTarget)}</span></p>
                  <p>{t('addOnlyHint')}</p>
                </div>
              )}
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('dateFinished')} *</Label>
              <Input
                type="date"
                value={completeDate}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setCompleteDate(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setCompleteTarget(null)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleComplete} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
